import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Modal from 'components/Modal'
import RoundedButton from 'components/RoundedButton'
import Checkmark from 'components/SVG/Checkmark'
import Plus from 'components/SVG/Plus'
import Spacer from 'components/Spacer'

import * as T from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`

interface IImgSrcProps {
  imgSrc: string
}
const Image = styled.div<IImgSrcProps>`
  width: 100%;
  height: 200px;
  display: flex;
  background: center / cover no-repeat url('${props => props.imgSrc}');
  border-radius: 10px;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const ServiceNameText = styled.div`
  font-size: 20px;
  font-weight: 900;
  line-height: 27px;
  color: ${props => props.theme.colors.neutral900};
`

const PriceText = styled.div`
  color: ${props => props.theme.colors.neutral900};
  font-weight: 800;
  font-size: 24px;
  line-height: 34px;
`

const DescriptionText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.theme.colors.black};
`

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

interface IProps {
  isServiceAdded: boolean | undefined
  service: T.IService
  visible: boolean
  addToCart?: boolean
  handleAddItem(service: T.IService): void
  setVisible(modalVisible: string): void
}

const MoreDetailModal: FunctionComponent<IProps> = ({
  isServiceAdded,
  service,
  visible,
  addToCart,
  handleAddItem,
  setVisible,
}) => {
  const theme = useContext(ThemeContext)

  const renderIcon = () => {
    if (isServiceAdded) {
      return (
        <Checkmark
          width={16}
          height={16}
          fill={theme.colors.neutral900}
        />
      )
    }

    return (
      <Plus
        width={9}
        height={9}
        fill={theme.colors.white}
      />
    )
  }

  const setModalVisible = (modalVisible: boolean) => setVisible(modalVisible ? service._id : '')

  const handleCloseModal = () => {
    setVisible('')
  }

  const handleClickAddToCart = () => handleAddItem(service)

  return (
    <Modal
      visible={visible}
      setVisible={setModalVisible}
      borderRadius={24}
      maxWidth={524}
      unsetScroll
    >
      <Container>
        <Image imgSrc={service.photo} />
        <Spacer
          size={16}
          tabletSize={32}
          desktopSize={32}
          desktopLargeSize={32}
        />
        <HeaderContainer>
          <ServiceNameText>{service.name}</ServiceNameText>
          <Spacer
            size={16}
            tabletSize={32}
            desktopSize={32}
            desktopLargeSize={32}
          />
          <PriceText>{`$${service.price.toFixed(2)}`}</PriceText>
        </HeaderContainer>
        <Spacer
          size={16}
          tabletSize={32}
          desktopSize={32}
          desktopLargeSize={32}
        />
        <DescriptionText>{service.description}</DescriptionText>
        <Spacer
          size={16}
          tabletSize={32}
          desktopSize={32}
          desktopLargeSize={32}
        />
        <ButtonsContainer>
          {addToCart && (
            <>
              <RoundedButton
                color={theme.colors.teal500}
                background={theme.colors.white}
                borderColor={theme.colors.teal500}
                borderWidth={1}
                height={44}
                radius={50}
                fullWidth
                text="close"
                onClick={handleCloseModal}
              />
              <Spacer size={16} />
              <RoundedButton
                color={isServiceAdded ? theme.colors.neutral900 : theme.colors.white}
                background={isServiceAdded ? theme.colors.teal100 : theme.colors.blue500}
                hoverBackground={isServiceAdded ? theme.colors.teal100 : theme.colors.blue400}
                height={44}
                radius={50}
                text={isServiceAdded ? 'Added' : 'add'}
                renderLeftIcon={renderIcon}
                onClick={handleClickAddToCart}
                iconPadding={10}
                fullWidth
                disabled={isServiceAdded}
              />
            </>
          )}
        </ButtonsContainer>
      </Container>
    </Modal>
  )
}

export default MoreDetailModal
